export enum LlmPrompt {
  AI_USAGE_PRIVACY_PRACTICE = 'ai-usage-privacy-practice',
  AI_USE_CASE_EVALUATOR = 'ai-use-case-evaluator',
  AI_USE_CASE_TEMPLATE = 'ai-use-case-template',
  AI_USE_CASE_VALIDATOR = 'ai-use-case-validator',
  PLANNING_EXERCISE = 'planning-exercise',
  PROMPTING_ASSESSMENT_CREATOR = 'prompting-assessment-creator',
  PROMPTING_ASSESSMENT_GRADER = 'prompting-assessment-grader',
  PROMPTING_MAD_LIB_GRADER = 'prompting-mad-lib-grader',
  PROMPT_WRITING_EVALUATOR = 'prompt-writing-evaluator',
  PROMPT_WRITING_FEEDBACK = 'prompt-writing-feedback',
  CONTENT_RECALL = 'content-recall',
}

export function isLlmPrompt(name: LlmPrompt | string): name is LlmPrompt {
  return Object.values(LlmPrompt).includes(name as LlmPrompt);
}

export enum Models {
  'Claude 3.5 Haiku' = 'claude-3-5-haiku-20241022',
  'Claude 3.5 Sonnet v2' = 'claude-3-5-sonnet-20241022',
  'Claude 3.7 Sonnet' = 'claude-3-7-sonnet-20250219',
  'Claude 4 Sonnet' = 'claude-sonnet-4-20250514',
  'GPT 4.1' = 'gpt-4.1',
  'GPT 4.1 mini' = 'gpt-4.1-mini',
  'GPT 4.1 nano' = 'gpt-4.1-nano',
  'Llama 3.3' = 'llama-3.3-70b-instruct',
  'Gemini 2.0 Flash Lite' = 'gemini-2.0-flash-lite-001',
  'Gemini 2.5 Pro Preview' = 'google/gemini-2.5-pro-preview',
}

export const DefaultModel = Models['Claude 3.7 Sonnet'];

const BedrockModels: Partial<Record<Models, string>> = {
  [Models['Claude 3.5 Haiku']]: 'us.anthropic.claude-3-5-haiku-20241022-v1:0',
  [Models['Claude 3.5 Sonnet v2']]: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0',
  [Models['Claude 3.7 Sonnet']]: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
  [Models['Claude 4 Sonnet']]: 'us.anthropic.claude-sonnet-4-20250514-v1:0',
};

const OpenRouterModels: Partial<Record<Models, string>> = {
  [Models['Claude 3.5 Haiku']]: 'anthropic/claude-3.5-haiku',
  [Models['Claude 3.5 Sonnet v2']]: 'anthropic/claude-3.5-sonnet',
  [Models['Claude 3.7 Sonnet']]: 'anthropic/claude-3.7-sonnet',
  [Models['Claude 4 Sonnet']]: 'anthropic/claude-sonnet-4',
  [Models['GPT 4.1']]: 'openai/gpt-4.1',
  [Models['GPT 4.1 mini']]: 'openai/gpt-4.1-mini',
  [Models['GPT 4.1 nano']]: 'openai/gpt-4.1-nano',
  [Models['Llama 3.3']]: 'meta-llama/llama-3.3-70b-instruct',
  [Models['Gemini 2.0 Flash Lite']]: 'google/gemini-2.0-flash-lite-001',
  [Models['Gemini 2.5 Pro Preview']]: 'google/gemini-2.5-pro-preview',
};

export function toOpenRouterModel(model: Models | string): string {
  return OpenRouterModels[model as Models] || model;
}

export function toBedrockModel(model: Models | string): string {
  return BedrockModels[model as Models] || model;
}

export function isBedrockModel(model: Models | string): boolean {
  return Object.values(BedrockModels).includes(model as string);
}

export function isOpenRouterModel(model: Models | string): boolean {
  return Object.values(OpenRouterModels).includes(model as string);
}

export function fromBedrockModel(model: string): Models | undefined {
  const entry = Object.entries(BedrockModels).find(([, value]) => value === model);
  return entry ? (entry[0] as Models) : undefined;
}

export function fromOpenRouterModel(model: string): Models | undefined {
  const entry = Object.entries(OpenRouterModels).find(([, value]) => value === model);
  return entry ? (entry[0] as Models) : undefined;
}

export function toNormalModelName(model: string) {
  return fromBedrockModel(model) || fromOpenRouterModel(model) || model;
}

export function getModelLabel(model: string) {
  return Object.entries(Models).find(([, value]) => value === model)?.[0] || model;
}
